import { cn } from "@/lib/utils";
import { useLayoutEffect, useRef, useState } from "react";

const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const ROLL_MS = 280;

/**
 * Text that rolls like a ticker when it changes: the old value slides up and
 * out, the new one slides up in and briefly flashes green. Works on any
 * string, not only numbers ("+20 (max 98)" → "+15").
 */
export const RollingValue = ({
  value,
  className,
}: {
  value: string;
  className?: string;
}) => {
  const [state, setState] = useState({
    current: value,
    previous: null as string | null,
    id: 0,
  });
  if (state.current !== value) {
    setState({ current: value, previous: state.current, id: state.id + 1 });
  }

  const outRef = useRef<HTMLSpanElement>(null);
  const inRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (state.id === 0) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setState((s) => ({ ...s, previous: null }));
      return;
    }
    inRef.current?.animate(
      [
        { transform: "translateY(90%)", opacity: 0 },
        { transform: "translateY(0)", opacity: 1 },
      ],
      { duration: ROLL_MS, easing: EASE },
    );
    const out = outRef.current?.animate(
      [
        { transform: "translateY(0)", opacity: 1 },
        { transform: "translateY(-90%)", opacity: 0 },
      ],
      { duration: ROLL_MS, easing: EASE, fill: "forwards" },
    );
    const id = state.id;
    const clear = () =>
      setState((s) => (s.id === id ? { ...s, previous: null } : s));
    if (out) out.finished.then(clear, () => {});
    else clear();
  }, [state.id]);

  return (
    <span
      className={cn(
        "inline-grid justify-items-end overflow-hidden [&>*]:[grid-area:1/1]",
        className,
      )}
    >
      {state.previous !== null && (
        <span key={`out-${state.id}`} ref={outRef} aria-hidden>
          {state.previous}
        </span>
      )}
      <span
        key={`in-${state.id}`}
        ref={inRef}
        className={cn(state.id > 0 && "animate-value-flash")}
      >
        {state.current}
      </span>
    </span>
  );
};
